import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Database } from '@/shared/types/database'

type UserResidence = {
  role: 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'USER'
  residence_id: string
}

type User = Database['public']['Tables']['users']['Row'] & {
  invitation_status?: 'pending' | 'active' | 'expired'
  invited_at?: string
  invitation_accepted_at?: string
  user_residences?: UserResidence[]
}

export function useUsers(agencyId?: string) {
  return useQuery({
    queryKey: ['users', agencyId],
    queryFn: async () => {
      if (!agencyId) return []

      // Récupérer les utilisateurs de l'agence avec leurs rôles
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          user_residences (
            role,
            residence_id
          )
        `)
        .eq('agency_id', agencyId)

      if (error) throw error
      return data as User[]
    },
    enabled: !!agencyId,
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      email: string
      firstName: string
      lastName: string
      role: 'ADMIN' | 'MANAGER' | 'ACCOUNTANT' | 'USER'
      residenceIds: string[]
      agencyId: string
    }) => {
      // Appeler l'edge function pour créer l'utilisateur
      // (L'edge function vérifie aussi les limites d'abonnement)
      const { data: result, error: funcError } = await supabase.functions.invoke('invite-user', {
        body: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          residenceIds: data.residenceIds,
          agencyId: data.agencyId,
        },
      })

      if (funcError) throw funcError
      if (result?.error) throw new Error(result.error)

      return result?.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.agencyId] })
      // Invalider également l'usage pour mettre à jour le compteur
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', variables.agencyId] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { userId: string; agencyId: string }) => {
      // Supprimer les associations user_residences
      const { error: residenceError } = await supabase
        .from('user_residences')
        .delete()
        .eq('user_id', data.userId)

      if (residenceError) throw residenceError

      // Supprimer l'utilisateur
      const { error: userError } = await supabase.auth.admin.deleteUser(data.userId)

      if (userError) throw userError

      return data.userId
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.agencyId] })
      queryClient.invalidateQueries({ queryKey: ['subscription-usage', variables.agencyId] })
    },
  })
}

export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { userId: string; email: string; agencyId: string }) => {
      // Appeler l'edge function pour renvoyer l'invitation
      const { data: result, error: funcError } = await supabase.functions.invoke(
        'resend-invitation',
        {
          body: {
            userId: data.userId,
            email: data.email,
          },
        }
      )

      if (funcError) throw funcError
      if (result?.error) throw new Error(result.error)

      return result?.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.agencyId] })
    },
  })
}
